/* QUICK JOB — book a load without leaving the War Room. The essentials only; anything
   fancier (extra mix lines, pump, surcharges, PO, quote) lives on the full Dispatch form. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isFleet, FLEET_UNITS, unitLabel } from "@/lib/industry";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const fieldCls = "w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2";

export const QuickJobDrawer = ({ open, onOpenChange, plantId, date, onCreated }) => {
  const { org } = useAuth();
  const fleet = isFleet(org);
  const [customers, setCustomers] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("repeat");
  const [suggestions, setSuggestions] = useState([]);
  const [f, setF] = useState({ customer_id: "", site_id: "", site_name: "", site_address: "", use_custom_site: false, mix_id: "", product: "", material_id: "", unit: "m3", notes: "", quantity_m3: 8, date: date, time_required: "09:00", trucks_needed: "" });

  useEffect(() => { setF((p) => ({ ...p, date })); }, [date]);

  const load = useCallback(async () => {
    try {
      const [c, m, mat, s] = await Promise.all([
        api.get("/customers"), api.get("/mixes"),
        isFleet(org) ? api.get("/materials").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get("/jobs/repeat-suggestions?limit=12").catch(() => ({ data: { suggestions: [] } })),
      ]);
      setCustomers(c.data || []);
      setMixes((m.data || []).filter((x) => x.active !== false));
      setMaterials((mat.data || []).filter((x) => x.active !== false));
      const sug = s.data.suggestions || [];
      setSuggestions(sug);
      setTab(sug.length ? "repeat" : "new");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load customers and mixes"); }
  }, [org]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const repeat = (s) => {
    const mat = fleet ? materials.find((m) => m.name === (s.product || s.mix_code)) : null;
    setF({
      customer_id: s.customer_id || "", site_id: s.site_id || "",
      site_name: s.site_name || s.site_address || "", site_address: s.site_address || "",
      use_custom_site: !s.site_id, mix_id: fleet ? "" : (s.mix_id || ""),
      product: fleet ? (s.product || s.mix_code || "") : "",
      material_id: mat ? mat.id : "", unit: mat ? (mat.unit || "m3") : (s.unit || "m3"), notes: "",
      quantity_m3: s.quantity_m3 || 8, date,
      time_required: s.time_required || "09:00",
      trucks_needed: s.trucks_needed ?? "",
    });
    setTab("new");
    toast.success(`${s.customer_name} · ${s.mix_code} loaded — check the date and time`);
  };

  const customer = useMemo(() => customers.find((c) => c.id === f.customer_id), [customers, f.customer_id]);
  const sites = customer?.sites || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id) return toast.error("Pick a customer");
    if (fleet ? !f.product.trim() : !f.mix_id) return toast.error(fleet ? "Enter a product / material" : "Pick a mix");
    if (!(Number(f.quantity_m3) > 0)) return toast.error("Quantity has to be more than zero");
    if (!f.use_custom_site && !f.site_id && sites.length > 0) return toast.error("Pick a site or tick 'different address'");
    if (f.use_custom_site && !f.site_name.trim()) return toast.error("Give the site a name");

    setBusy(true);
    try {
      const payload = {
        customer_id: f.customer_id,
        quantity_m3: Number(f.quantity_m3),
        date: f.date,
        time_required: f.time_required,
        plant_id: plantId,
      };
      if (fleet) { payload.product = f.product.trim(); payload.notes = f.notes || ""; payload.unit = f.unit || "m3"; if (f.material_id) payload.material_id = f.material_id; }
      else { payload.mix_id = f.mix_id; }
      if (f.use_custom_site || sites.length === 0) {
        payload.site_name = f.site_name.trim();
        payload.site_address = f.site_address.trim();
        payload.save_site = true;
      } else {
        payload.site_id = f.site_id;
      }
      if (f.trucks_needed !== "" && !isNaN(parseInt(f.trucks_needed, 10))) payload.trucks_needed = parseInt(f.trucks_needed, 10);
      await api.post("/jobs", payload);
      toast.success("Job on the board");
      setF({ ...f, site_id: "", site_name: "", site_address: "", use_custom_site: false, quantity_m3: 8, trucks_needed: "" });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not create the job");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md rounded-none bg-surface border-industrial overflow-y-auto" data-testid="quick-job-drawer">
        <SheetHeader><SheetTitle className="heading uppercase tracking-wide">New Job</SheetTitle></SheetHeader>

        <div className="flex gap-1 mt-3" data-testid="qj-tabs">
          {[["repeat", `Repeat${suggestions.length ? ` (${suggestions.length})` : ""}`], ["new", "Blank job"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`qj-tab-${k}`}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-2 border ${tab === k ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "repeat" ? (
          <div className="mt-3 space-y-2" data-testid="qj-repeat-list">
            <p className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Tap a regular to book it again</p>
            {suggestions.length === 0 && (
              <div className="border border-dashed border-industrial p-6 text-center mono text-[11px] uppercase tracking-widest text-muted-industrial" data-testid="qj-repeat-empty">
                No past jobs yet
              </div>
            )}
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => repeat(s)} data-testid={`qj-repeat-${i}`}
                className="w-full text-left border border-industrial p-3 hover:border-primary-orange group">
                <div className="flex items-start gap-2">
                  <RotateCcw className="w-3.5 h-3.5 text-primary-orange mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold uppercase truncate group-hover:text-primary-orange">{s.customer_name}</div>
                    <div className="mono text-[11px] text-muted-industrial truncate">{s.site_name || "no site"}</div>
                    <div className="mono text-[11px] mt-0.5">
                      <span className="text-primary-orange">{s.mix_code}</span> · {s.quantity_m3} m³{s.time_required ? ` · ${s.time_required}` : ""}
                    </div>
                  </div>
                  <div className="ml-auto text-right shrink-0">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">{s.times}×</div>
                    <div className="mono text-[10px] text-muted-industrial">{s.last_date}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-3 mt-4">
          <label className="block">
            <span className="label-tag">Customer *</span>
            <select data-testid="qj-customer" value={f.customer_id}
              onChange={(e) => setF({ ...f, customer_id: e.target.value, site_id: "" })} className={fieldCls}>
              <option value="">— select —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          {f.customer_id && !f.use_custom_site && sites.length > 0 && (
            <label className="block">
              <span className="label-tag">Site *</span>
              <select data-testid="qj-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className={fieldCls}>
                <option value="">— select —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.address}</option>)}
              </select>
            </label>
          )}

          {f.customer_id && (
            <label className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-muted-industrial">
              <input type="checkbox" data-testid="qj-custom-site" checked={f.use_custom_site || sites.length === 0}
                disabled={sites.length === 0}
                onChange={(e) => setF({ ...f, use_custom_site: e.target.checked, site_id: "" })} />
              Different address this time
            </label>
          )}

          {(f.use_custom_site || (f.customer_id && sites.length === 0)) && (
            <div className="grid gap-2">
              <label className="block"><span className="label-tag">Site name *</span>
                <input data-testid="qj-site-name" value={f.site_name} onChange={(e) => setF({ ...f, site_name: e.target.value })} placeholder="e.g. 14 Jackson St slab" className={fieldCls} /></label>
              <label className="block"><span className="label-tag">Address</span>
                <input data-testid="qj-site-address" value={f.site_address} onChange={(e) => setF({ ...f, site_address: e.target.value })} className={fieldCls} /></label>
            </div>
          )}

          {fleet ? (
            <label className="block"><span className="label-tag">Product / Material *</span>
              {materials.length > 0 ? (
                <select data-testid="qj-product" value={f.material_id}
                  onChange={(e) => {
                    const m = materials.find((x) => x.id === e.target.value);
                    setF({ ...f, material_id: e.target.value, product: m ? m.name : "", unit: m ? (m.unit || "m3") : f.unit });
                  }} className={fieldCls}>
                  <option value="">— select material —</option>
                  {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({unitLabel(m.unit)})</option>)}
                </select>
              ) : (
                <>
                  <input data-testid="qj-product" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} placeholder="e.g. Pine bark mulch, 20t gravel" className={fieldCls} />
                  <a href="/materials" className="block mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange mt-1">Tip: add your products in Materials for a quick pick list</a>
                </>
              )}
            </label>
          ) : (
            <label className="block"><span className="label-tag">Mix *</span>
              <select data-testid="qj-mix" value={f.mix_id} onChange={(e) => setF({ ...f, mix_id: e.target.value })} className={fieldCls}>
                <option value="">— select —</option>
                {mixes.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </label>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label-tag">{fleet ? "Quantity *" : "Volume (m³) *"}</span>
              <input type="number" step="0.2" min="0.2" data-testid="qj-qty" value={f.quantity_m3}
                onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setF({ ...f, quantity_m3: (Math.round(v / 0.2) * 0.2).toFixed(1) }); }}
                onChange={(e) => setF({ ...f, quantity_m3: e.target.value })} className={fieldCls} /></label>
            {fleet ? (
              <label className="block"><span className="label-tag">Unit</span>
                <select data-testid="qj-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={fieldCls}>
                  {FLEET_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </label>
            ) : (
              <label className="block"><span className="label-tag">Trucks</span>
                <input type="number" min="1" data-testid="qj-trucks" value={f.trucks_needed} placeholder="auto"
                  onChange={(e) => setF({ ...f, trucks_needed: e.target.value })} className={fieldCls} /></label>
            )}
            <label className="block"><span className="label-tag">Date *</span>
              <input type="date" data-testid="qj-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={fieldCls} /></label>
            <label className="block"><span className="label-tag">On site</span>
              <input type="time" data-testid="qj-time" value={f.time_required} onChange={(e) => setF({ ...f, time_required: e.target.value })} className={fieldCls} /></label>
          </div>

          {fleet && (
            <label className="block"><span className="label-tag">Notes</span>
              <textarea data-testid="qj-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Access, contact on site, special instructions…" className={fieldCls} />
            </label>
          )}

          <button type="submit" disabled={busy} data-testid="qj-submit"
            className="w-full bg-primary-orange text-black font-black uppercase tracking-widest text-sm py-3 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add to the board
          </button>

          {!fleet && (
          <a href="/orders?new=1" data-testid="qj-more-options"
            className="block text-center mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange pt-1">
            More options — pump, PO, extra mixes, quote <ExternalLink className="w-3 h-3 inline" />
          </a>
          )}
        </form>
        )}
      </SheetContent>
    </Sheet>
  );
};
